import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import actions from "@/actions";
import { getConstructionId } from "@/lib/common";
import { TextButton } from "@/sx-layout/common/Button/TextButton";
import { Header } from "@/sx-layout/common/Header";
import { DeleteConfirmModal } from "@/sx-layout/common/Modal";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/sx-layout/common/Table";
import { ToggleWithWords } from "@/sx-layout/common/ToggleWithWords";
import { DeletePlotPlanProps } from "@/sx-layout/components/settings/plotPlanSetting/actions/types";
import { PlotPlanEditor } from "@/sx-layout/components/settings/plotPlanSetting/components/PlotPlanEditor";
import { usePlotPlanSetting } from "@/sx-layout/components/settings/plotPlanSetting/hooks/usePlotPlanSetting";
import { usePlotPlans } from "@/sx-layout/components/settings/plotPlanSetting/hooks/usePlotPlans";
import { PlotPlan, PlotPlanType } from "@/sx-layout/components/settings/plotPlanSetting/models";
import { qualityMasters } from "@/sx-layout/components/settings/plotPlanSetting/utils/quality";

export const PlotPlanSetting: FC = () => {
  const [isOpenEditor, setIsOpenEditor] = useState(false);
  const [editPlotPlan, setEditPlotPlan] = useState<PlotPlan>();

  const [deleteTargetPlotPlan, setDeleteTargetPlotPlan] = useState<PlotPlan>(null);

  const { plotPlanType, changePlotPlanMapType } = usePlotPlanSetting();

  const dispatch = useDispatch();
  const { plotPlans, refreshPlotPlans } = usePlotPlans();

  const deletePlotPlan = useCallback(
    (props: DeletePlotPlanProps) => dispatch(actions.plotPlan.deletePlotPlan(props)),
    []
  );

  const constructionId = getConstructionId();

  useEffect(() => {
    // 一覧取得
    if (constructionId) {
      refreshPlotPlans();
    }
    // プロットプラン連携設定クリア
    dispatch(actions.plotPlanMapping.setPlotPlanIdForMapping(0));
  }, [constructionId]);

  const { t } = useTranslation();

  return (
    <div className="bg-[#f3f2ef] min-h-screen">
      <Header />
      <div className="px-[32px]">
        <div className="flex items-center space-x-8">
          <h2 className="my-[16px] font-bold text-[16px]">{t("plot_plan")}</h2>
          <button
            type="button"
            onClick={() => setIsOpenEditor(true)}
            className="flex items-center justify-center pb-1 cursor-pointer"
          >
            <img className="w-[24px] h-[24px]" src="./img/icon-add-black.svg" alt="plus"></img>
          </button>
          <ToggleWithWords
            id="map-type"
            onChange={(_, v) => changePlotPlanMapType(v ? PlotPlanType.DETAIL : PlotPlanType.OVERVIEW)}
            isDefaultOn={plotPlanType === PlotPlanType.DETAIL}
            labelOn={t("map_detail")}
            labelOff={t("map_overview")}
          />
        </div>
        {plotPlans.length > 0 && (
          <Table>
            <Thead>
              <Tr>
                <Th className="w-[280px]">{t("plot_plan_name")}</Th>
                <Th className="w-[150px]">{t("floor_plan_image_quality")}</Th>
                <Th className="w-[100px]">{t("image_size")}</Th>
                <Th>{t("note")}</Th>
                {plotPlanType === PlotPlanType.OVERVIEW && <Th className="w-[80px]">{t("mapping")}</Th>}
                <Th className="w-[80px]">{t("edit")}</Th>
                <Th className="w-[80px]">{t("delete")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {plotPlans
                .filter((plotPlan) => plotPlan.plot_plan_type === plotPlanType)
                .map((plotPlan) => (
                  <Tr key={plotPlan.plot_plan_id}>
                    <Td>{plotPlan.plot_plan_name}</Td>
                    <Td>{t(qualityMasters.find((quality) => quality.value === plotPlan.quality)?.label)}</Td>
                    <Td className="text-right">
                      {/* TODO: APIからの返却値がKB単位になっているか確認したい */}
                      {plotPlan.file_size.toLocaleString("ja-JP")}
                    </Td>
                    <Td>{plotPlan.note}</Td>
                    {plotPlanType === PlotPlanType.OVERVIEW && (
                      <Td className="text-center">
                        <TextButton
                          onClick={() => {
                            dispatch(actions.plotPlanMapping.setPlotPlanIdForMapping(plotPlan.plot_plan_id));
                            dispatch(actions.layoutApp.changeLayoutMenu("plot_plan_mapping"));
                          }}
                        >
                          {t("settings")}
                        </TextButton>
                      </Td>
                    )}
                    <Td className="text-center">
                      <TextButton
                        onClick={() => {
                          setIsOpenEditor(true);
                          setEditPlotPlan(plotPlan);
                        }}
                      >
                        {t("edit")}
                      </TextButton>
                    </Td>
                    <Td className="text-center">
                      <TextButton
                        onClick={() => {
                          setDeleteTargetPlotPlan(plotPlan);
                        }}
                      >
                        {t("delete")}
                      </TextButton>
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
        )}
      </div>
      {isOpenEditor && (
        <PlotPlanEditor
          editPlotPlan={editPlotPlan}
          onClose={() => {
            setIsOpenEditor(false);
            setEditPlotPlan(undefined);
            refreshPlotPlans();
          }}
        />
      )}
      {deleteTargetPlotPlan && (
        <DeleteConfirmModal
          message={`${deleteTargetPlotPlan.plot_plan_name}` + t("delete_confirmation")}
          onClose={() => {
            setDeleteTargetPlotPlan(null);
          }}
          onSubmit={() => {
            deletePlotPlan({
              input: { plot_plan_id: deleteTargetPlotPlan.plot_plan_id },
              onSuccess: () => {
                refreshPlotPlans();
                setDeleteTargetPlotPlan(null);
              },
              onFailure: () => {
                setDeleteTargetPlotPlan(null);
              },
            });
          }}
        />
      )}
    </div>
  );
};
